import React, { useState, useEffect, useMemo } from "react";
import { ValidationView } from "components/ezpay/validate/validation-view";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ForgeThemeProvider, Spinner } from "kubra-ux-forge";
import routing from "routing";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import {
  AnalyticsProvider,
  createAnalyticsInstance,
  useKubraTracking,
} from "kubra-ui-lib-mfe";
import { googleAnalyticsCode } from "config";
import { CsrDirectLanding } from "components/ezpay/validate/csr-direct-landing";
import { SessionProvider } from "components/session-provider";
import { PublicRoute } from "components/public-route";
import { ProtectedRoute } from "components/protected-route";
import { UnsubscribeAlerts } from "components/ezpay/common/unsubscribe-alerts";
import { PaySlip } from "components/ezpay/retail-cash/pay-slip";
import { RetailerLocation } from "components/ezpay/retail-cash/retailer-location";
import { BalanceFrame } from "components/ezpay/secure/balance/balance-frame";
import { Payment } from "components/ezpay/secure/payment";
import { PaymentReceipt } from "components/ezpay/secure/common/payment-receipt";
import { RecentPayments } from "components/ezpay/secure/common/recent-payments";
import { Menu } from "components/ezpay/common/menu";
import { LinkExpired } from "components/ezpay/common/link-expired";
import { DirectLanding } from "components/ezpay/validate/direct-landing";
import { PageNotFound } from "components/ezpay/common/404-page-not-found";
import { SmsDirectLanding } from "components/ezpay/validate/sms-direct-landing";
import { SmsBarcodeView } from "components/ezpay/secure/common/retail-cash-flow/sms-barcode-view";
import { PayableDetails } from "components/ezpay/payables/payable-details";
import { InvoiceListView } from "components/ezpay/payables/invoice-list-view";
import { PayableReviewPage } from "components/ezpay/secure/payable-review-page";
import { PaymentSuccess } from "components/ezpay/secure/payment-success";
import { LookupsView } from "components/ezpay/payables/lookups-view";
import { RcpBarcodeView } from "components/ezpay/secure/common/retail-cash-flow/rcp-barcode-view";
import { useBiller, useLabels } from "hooks";
import { PayableSessionProvider } from "components/payable-session-provider";
import { PayableDashboard } from "components/ezpay/secure/payable-dashboard/payable-dashboard";
import { PayableRecentPayments } from "components/ezpay/secure/common/payable-recent-payments";
import { SelectPayableTypeView } from "components/ezpay/select-payable-type/select-payable-type-view";
import { useSelector, useDispatch } from "react-redux";
import { getBillerLoadStatus } from "selectors";
import { EMPTY, ERROR } from "constants/Status";
import { setFingerprint, setIsMobile } from "actions/appStateActions";
import { PayablePayment } from "components/ezpay/secure/payable-payment";
import { PayableCsrDirectLanding } from "components/ezpay/validate/payable-csr-direct-landing";
import { getDomain, mapUiLanguageToSessionLanguage } from "utilities/helper";
import { useTranslation } from "react-i18next";
import { PayableHeader } from "components/ezpay/common/payable-header";
import { PayableFooter } from "components/ezpay/common/payable-footer";

export const App = (): JSX.Element => {
  const [width, setWidth] = useState<number>(window.innerWidth);

  const { loadBiller, getBiller } = useBiller();
  const loadBillerStatus = useSelector(getBillerLoadStatus);
  const biller = getBiller();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (loadBillerStatus === EMPTY) {
      loadBiller();
    } else if (loadBillerStatus === ERROR) {
      console.log("failed to retrieve instance");
      navigate(routing.notFound.index());
    }
  }, [loadBillerStatus, loadBiller, navigate]);

  const { i18n } = useTranslation();
  const { loadLabels } = useLabels();

  useEffect(() => {
    if (!biller || !biller.clientId || !biller.realm || !biller.instanceId) {
      return;
    }

    const domain = getDomain();
    const { clientId, realm, instanceId } = biller;
    const language = mapUiLanguageToSessionLanguage(
      i18n.resolvedLanguage ?? "en"
    );

    loadLabels(clientId, instanceId, domain, realm, language, biller.payableTypeDefinitions ?? [], biller.featureFlags?.payableUi ?? "false");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biller]);

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
  };

  const isMobile = width <= 768;

  useEffect(() => {
    const setFp = async () => {
      const fp = await FingerprintJS.load();
      const { visitorId } = await fp.get();
      dispatch(setFingerprint(visitorId));
      dispatch(setIsMobile(width <= 768));
    };

    setFp();

    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [navigate, biller, dispatch, width]);

  const analyticsInstance = useMemo(
    () => createAnalyticsInstance("EZ-PAY", googleAnalyticsCode),
    []
  );

  const { Track } = useKubraTracking({
    commonTrackingData: { env: process.env.NODE_ENV, mfe: "EZ-PAY" },
    analytics: analyticsInstance,
  });

  // TODO: may be we should also wait until the labels are loaded?

  if (
    (biller === null || biller === undefined) &&
    location.pathname !== routing.notFound.index()
  ) {
    return <Spinner size="large" variant="primary" />;
  }

  return (
    // <IntlProvider locale={navigator.language} messages={messages[language]}>
    <div
      className={
        biller?.featureFlags?.payableUi?.toLowerCase() === "true"
          ? "ez-pay-background-payable"
          : "ez-pay-background-account"
      }
    >
      <AnalyticsProvider analytics={analyticsInstance}>
        <ForgeThemeProvider>
          {biller?.featureFlags?.payableUi?.toLowerCase() === "true" ? (
            <div className="payable-header">
              <PayableHeader isMobile={isMobile} />
            </div>
          ) : (
            <></>
          )}
          <Track>
            <Routes>
              {biller?.featureFlags?.payableUi?.toLowerCase() === "true" ? (
                // Payable UI is true, show the default routes for payable flow
                <Route element={<PayableSessionProvider />}>
                  <Route>
                    <Route
                      index
                      element={
                        <PublicRoute element={<SelectPayableTypeView />} />
                      }
                    />
                    <Route
                      path={routing.terms.index()}
                      element={
                        <PublicRoute element={<SelectPayableTypeView />} />
                      }
                    />
                    <Route
                      path={routing.privacy.index()}
                      element={
                        <PublicRoute element={<SelectPayableTypeView />} />
                      }
                    />
                    <Route
                      path={routing.lookup.index()}
                      element={<PublicRoute element={<LookupsView />} />}
                    />
                    <Route
                      path={routing.unsubscribeAlerts.index() + "/*"}
                      element={<PublicRoute element={<UnsubscribeAlerts />} />}
                    />
                    <Route
                      path={routing.payableDetails.index()}
                      element={<ProtectedRoute element={<PayableDetails />} />}
                    />
                    <Route
                      path={routing.invoiceList.index()}
                      element={<ProtectedRoute element={<InvoiceListView />} />}
                    />
                    <Route
                      index
                      path={routing.payableDashboard.index()}
                      element={
                        <ProtectedRoute element={<PayableDashboard />} />
                      }
                    />
                    <Route
                      path={routing.payableReview.index()}
                      element={
                        <ProtectedRoute element={<PayableReviewPage />} />
                      }
                    />
                    <Route
                      path={routing.paymentSuccess.index()}
                      element={<ProtectedRoute element={<PaymentSuccess />} />}
                    />
                    <Route
                      path={routing.payableRecentPayments.index()}
                      element={
                        <ProtectedRoute element={<PayableRecentPayments />} />
                      }
                    />

                    {/* TODO: there's probably no need for separate components for direct landing.
                      The code inside these direct landing components can live in the PayableSession component. */}
                    <Route
                      path={routing.payableCsrDirectLanding.index() + "/*"}
                      element={<PayableCsrDirectLanding />}
                    />
                    <Route
                      path={routing.payableDirectLanding.index() + "/*"}
                      element={<PayablePayment />}
                    />
                    <Route
                      path={routing.payablePayment.index() + "/*"}
                      element={<PayablePayment />}
                    />
                  </Route>
                </Route>
              ) : (
                // Payable UI is disabled, show the default routes for old account flow
                <Route element={<SessionProvider />}>
                  <Route
                    path={routing.payslip.index()}
                    element={<PublicRoute element={<PaySlip />} />}
                  />
                  <Route
                    path={routing.retailerLocation.index()}
                    element={<RetailerLocation />}
                  />
                  <Route
                    path={routing.csrDirectLanding.index() + "/*"}
                    element={<CsrDirectLanding />}
                  />
                  <Route
                    path={routing.directLanding.index() + "/*"}
                    element={<DirectLanding />}
                  />
                  <Route
                    path={routing.barcode.index() + "/*"}
                    element={<SmsDirectLanding />}
                  />

                  <Route element={<Menu isMobile={isMobile} />}>
                    <Route
                      index
                      element={
                        <PublicRoute
                          navToPath={routing.balance.index()}
                          element={<ValidationView />}
                        />
                      }
                    />
                    <Route
                      path={routing.terms.index()}
                      element={
                        <PublicRoute
                          navToPath={routing.balance.index()}
                          element={<ValidationView />}
                        />
                      }
                    />
                    <Route
                      path={routing.privacy.index()}
                      element={
                        <PublicRoute
                          navToPath={routing.balance.index()}
                          element={<ValidationView />}
                        />
                      }
                    />
                    <Route
                      path={routing.payment.index() + "/*"}
                      element={<PublicRoute element={<Payment />} />}
                    />
                    <Route
                      path={routing.balance.index()}
                      element={<ProtectedRoute element={<BalanceFrame />} />}
                    />
                    <Route
                      path={routing.paymentReceipt.index()}
                      element={<ProtectedRoute element={<PaymentReceipt />} />}
                    />
                    <Route
                      path={routing.unsubscribeAlerts.index() + "/*"}
                      element={<PublicRoute element={<UnsubscribeAlerts />} />}
                    />
                    <Route
                      path={routing.recentPayments.index()}
                      element={<ProtectedRoute element={<RecentPayments />} />}
                    />
                    <Route
                      path={routing.linkExpired.index()}
                      element={<LinkExpired></LinkExpired>}
                    />
                    <Route
                      path={routing.rcpBarcode.index()}
                      element={<ProtectedRoute element={<SmsBarcodeView />} />}
                    />
                    <Route
                      path={routing.rcpBarcodeView.index()}
                      element={<ProtectedRoute element={<RcpBarcodeView />} />}
                    />
                  </Route>
                </Route>
              )}
              {/* 404 page */}
              <Route
                path={routing.notFound.index()}
                element={<PageNotFound />}
              />
              <Route path="/*" element={<PageNotFound />} />
            </Routes>
          </Track>
        </ForgeThemeProvider>
      </AnalyticsProvider>
      {biller?.featureFlags?.payableUi?.toLowerCase() === "true" ? (
        <div className="ez-pay-footer">
          <PayableFooter isMobile={isMobile} />
        </div>
      ) : null}
    </div>
  );
};
